import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { db } from '../firebase';
import { collection, query, orderBy, onSnapshot, getDocs, limit } from 'firebase/firestore';
import { useAuth } from './AuthContext';
import { listUsers } from '../services/listUsers';
import { fetchAuditLogs } from '../services/auditLogService';
import { API_BASE_URL } from '../config';

const DataContext = createContext();
const RefreshContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const useRefresh = () => {
  const context = useContext(RefreshContext);
  if (!context) {
    throw new Error('useRefresh must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const { user, getIdToken: getAuthIdToken } = useAuth();
  
  // Shared data states
  const [allDocuments, setAllDocuments] = useState([]);
  const [documentsLoaded, setDocumentsLoaded] = useState(false);
  
  const [users, setUsers] = useState([]);
  const [usersLoaded, setUsersLoaded] = useState(false);

  const [logs, setLogs] = useState([]);
  const [logsLoaded, setLogsLoaded] = useState(false);

  const [securityAlerts, setSecurityAlerts] = useState([]);
  const [securityAlertsLoaded, setSecurityAlertsLoaded] = useState(false);

  const [requests, setRequests] = useState([]);
  const [requestsLoaded, setRequestsLoaded] = useState(false);

  const [purchases, setPurchases] = useState([]);
  const [purchasesLoaded, setPurchasesLoaded] = useState(false);

  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    contentUsers: 0,
    totalDocuments: 0,
    userGrowth: 0,
    activeUserGrowth: 0,
    securityAlerts: 0,
    systemHealth: 0,
    uptime: '0%',
    serverStatus: 'online',
    pendingDocuments: 0,
    uploadedBarangays: [],
    avgResponseTime: 0
  });
  const [recentUsers, setRecentUsers] = useState([]);
  const [statsLoaded, setStatsLoaded] = useState(false);
  
  const [securityMetricsLoaded, setSecurityMetricsLoaded] = useState(false);
  const [loading, setLoading] = useState(false);

  // Auto-refresh loop states
  const [nextRefresh, setNextRefresh] = useState(60);
  const [refreshCycle, setRefreshCycle] = useState(0);

  // Helper to get Firebase token
  const getFirebaseToken = useCallback(async () => {
    try {
      if (!user) return null;
      if (typeof getAuthIdToken === 'function') return await getAuthIdToken();
      return null;
    } catch (error) {
      console.error('Error getting Firebase token:', error);
      return null;
    }
  }, [user, getAuthIdToken]);

  // Update request status — optimistic update first, then confirm via backend
  const updateRequestStatus = useCallback(async (requestId, status) => {
    // Optimistically update local state immediately
    setRequests(prev => prev.map(r => r.id === requestId ? { ...r, status } : r));

    try {
      const token = await getFirebaseToken();
      if (!token) {
        // Revert optimistic update
        setRequests(prev => prev.map(r => r.id === requestId ? { ...r, status: r._prevStatus || r.status } : r));
        return { ok: false, error: 'Not authenticated' };
      }

      const reqObj = requests.find(r => r.id === requestId);
      const collectionType = reqObj?.collectionType || "accessRequests";

      const response = await fetch(`${API_BASE_URL}/api/requests/${requestId}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status, collectionType }),
      });

      const data = await response.json();
      if (!response.ok) {
        // Revert optimistic update on failure
        setRequests(prev => prev.map(r => r.id === requestId ? { ...r, status: r._prevStatus || r.status } : r));
        console.error('Error updating request status:', data.error);
        return { ok: false, error: data.error };
      }
      return { ok: true };
    } catch (err) {
      // Revert optimistic update on error
      setRequests(prev => prev.map(r => r.id === requestId ? { ...r, status: r._prevStatus || r.status } : r));
      console.error('Error updating request status:', err);
      return { ok: false, error: err.message };
    }
  }, [getFirebaseToken]);

  // Delete request — optimistic update first, then confirm via backend
  const deleteRequest = useCallback(async (requestId) => {
    // Optimistically remove from local state immediately
    const removed = requests.find(r => r.id === requestId);
    setRequests(prev => prev.filter(r => r.id !== requestId));

    try {
      const token = await getFirebaseToken();
      if (!token) {
        // Revert optimistic removal
        if (removed) setRequests(prev => [...prev, removed].sort((a, b) => {
          const t = v => v?.toMillis?.() ?? (v instanceof Date ? v.getTime() : Number(v) || 0);
          return t(b.createdAt) - t(a.createdAt);
        }));
        return { ok: false, error: 'Not authenticated' };
      }

      const reqObj = removed || {};
      const collectionType = reqObj.collectionType || "accessRequests";

      const response = await fetch(`${API_BASE_URL}/api/requests/${requestId}?type=${collectionType}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (!response.ok) {
        // Revert optimistic removal on failure
        if (removed) setRequests(prev => [...prev, removed].sort((a, b) => {
          const t = v => v?.toMillis?.() ?? (v instanceof Date ? v.getTime() : Number(v) || 0);
          return t(b.createdAt) - t(a.createdAt);
        }));
        console.error('Error deleting request:', data.error);
        return { ok: false, error: data.error };
      }
      return { ok: true };
    } catch (err) {
      // Revert optimistic removal on error
      if (removed) setRequests(prev => [...prev, removed].sort((a, b) => {
        const t = v => v?.toMillis?.() ?? (v instanceof Date ? v.getTime() : Number(v) || 0);
        return t(b.createdAt) - t(a.createdAt);
      }));
      console.error('Error deleting request:', err);
      return { ok: false, error: err.message };
    }
  }, [getFirebaseToken, requests]);

  // Fetch dashboard statistics
  const fetchDashboardStats = useCallback(async () => {
    try {
      const token = await getFirebaseToken();
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      const response = await fetch('https://avisa-huja.onrender.com/api/analytics/dashboard-stats', { headers });
      
      if (!response.ok && (response.status === 401 || response.status === 403)) {
        const publicResponse = await fetch('https://avisa-huja.onrender.com/api/public/dashboard-stats');
        if (publicResponse.ok) {
          const publicData = await publicResponse.json();
          if (publicData.ok) {
            setStats(prev => ({
              ...prev,
              totalUsers: publicData.stats.totalUsers,
              activeUsers: publicData.stats.activeUsers,
              contentUsers: publicData.stats.contentUsers,
              totalDocuments: publicData.stats.totalDocuments,
              userGrowth: publicData.stats.userGrowth,
              activeUserGrowth: publicData.stats.activeUserGrowth,
              uploadedBarangays: publicData.stats.uploadedBarangays || []
            }));
            setRecentUsers(publicData.recentUsers || []);
            setStatsLoaded(true);
            return;
          }
        }
      }
      
      if (response.ok) {
        const data = await response.json();
        if (data.ok) {
          let serverUptime = '';
          if (data.stats.serverUptime) {
            const num = parseFloat(String(data.stats.serverUptime).replace('%',''));
            if (!isNaN(num)) {
              serverUptime = `${Math.round(num)}%`;
            } else {
              serverUptime = String(data.stats.serverUptime).replace(/\.\d+/, '').replace('%','') + '%';
            }
          }
          setStats(prev => ({
            ...prev,
            totalUsers: data.stats.totalUsers,
            activeUsers: data.stats.activeUsers,
            contentUsers: data.stats.contentUsers,
            totalDocuments: data.stats.totalDocuments,
            userGrowth: data.stats.userGrowth,
            activeUserGrowth: data.stats.activeUserGrowth,
            uploadedBarangays: data.stats.uploadedBarangays || [],
            serverUptime,
            databasePerformance: data.stats.databasePerformance || null,
            serverStatus: data.stats.serverStatus || 'unknown'
          }));
          setRecentUsers(data.recentUsers || []);
          setStatsLoaded(true);
        }
      }
    } catch (err) {
      console.error('Error fetching dashboard stats:', err);
    }
  }, [getFirebaseToken]);

  // Fetch security metrics
  const fetchSecurityMetrics = useCallback(async () => {
    try {
      const token = await getFirebaseToken();
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      const response = await fetch('https://avisa-huja.onrender.com/api/analytics/security-metrics', { headers });
      if (response.ok) {
        const data = await response.json();
        if (data.ok) {
          setStats(prev => ({
            ...prev,
            securityAlerts: typeof data.securityAlerts === 'number' ? data.securityAlerts : prev.securityAlerts,
            systemHealth: data.systemHealth || prev.systemHealth,
            uptime: data.uptime || prev.uptime,
            serverStatus: data.serverStatus || prev.serverStatus,
            pendingDocuments: typeof data.pendingDocuments === 'number' ? data.pendingDocuments : prev.pendingDocuments,
            avgResponseTime: data.avgResponseTime || 0,
            databasePerformance: data.databasePerformance || prev.databasePerformance
          }));
          setSecurityMetricsLoaded(true);
        }
      }
    } catch (err) {
      console.error('Error fetching security metrics:', err);
      setStats(prev => ({ 
        ...prev, 
        serverStatus: 'offline', 
        uptime: '0%', 
        systemHealth: 0,
        securityAlerts: 0,
        pendingDocuments: 0
      }));
    }
  }, [getFirebaseToken]);

  // Fetch users (if system admin)
  const fetchUsers = useCallback(async () => {
    try {
      const data = await listUsers();
      setUsers(data);
      setUsersLoaded(true);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  }, []);

  // Fetch audit logs (if system admin)
  const fetchLogs = useCallback(async () => {
    try {
      const data = await fetchAuditLogs({ max: 200 });
      setLogs(data);
      setLogsLoaded(true);
    } catch (err) {
      console.error('Error fetching audit logs:', err);
    }
  }, []);

  // Fetch security alerts (if system admin)
  const fetchSecurityAlerts = useCallback(async () => {
    try {
      const token = await getFirebaseToken();
      if (!token) return;

      const response = await fetch(`https://avisa-huja.onrender.com/api/security/alerts?page=1&limit=200`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setSecurityAlerts(data.alerts || []);
        setSecurityAlertsLoaded(true);
      }
    } catch (err) {
      console.error('Error fetching security alerts:', err);
    }
  }, [getFirebaseToken]);

  // Update security alert status
  const updateSecurityAlertStatus = useCallback(async (alertId, status) => {
    try {
      const token = await getFirebaseToken();
      if (!token) return { ok: false, error: 'Not authenticated' };

      const response = await fetch(`https://avisa-huja.onrender.com/api/security/alerts/${alertId}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status }),
      });
      
      if (response.ok) {
        await Promise.all([
          fetchSecurityAlerts(),
          fetchSecurityMetrics()
        ]);
        return { ok: true };
      }
      const data = await response.json();
      return { ok: false, error: data.error };
    } catch (err) {
      console.error('Error updating security alert status:', err);
      return { ok: false, error: err.message };
    }
  }, [getFirebaseToken, fetchSecurityAlerts]);

  // Delete security alert
  const deleteSecurityAlert = useCallback(async (alertId) => {
    try {
      const token = await getFirebaseToken();
      if (!token) return { ok: false, error: 'Not authenticated' };

      const response = await fetch(`https://avisa-huja.onrender.com/api/security/alerts/${alertId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        await Promise.all([
          fetchSecurityAlerts(),
          fetchSecurityMetrics()
        ]);
        return { ok: true };
      }
      const data = await response.json();
      return { ok: false, error: data.error };
    } catch (err) {
      console.error('Error deleting security alert:', err);
      return { ok: false, error: err.message };
    }
  }, [getFirebaseToken, fetchSecurityAlerts]);

  // Fetch LGU access requests
  const fetchRequests = useCallback(async () => {
    try {
      const token = await getFirebaseToken();
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/api/requests`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.ok) {
          setRequests(data.requests || []);
          setRequestsLoaded(true);
        }
      }
    } catch (err) {
      console.error('Error fetching requests:', err);
    }
  }, [getFirebaseToken]);

  // Subscribe to recent purchases (if system admin)
  const subscribePurchases = useCallback(() => {
    if (!user || (user.role !== 'system_admin' && user.role !== 'sysadmin')) return () => {};

    const q = query(collection(db, "recentPurchases"), limit(100));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const docs = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          date: data.date?.toMillis?.() || (typeof data.date === 'number' ? data.date : (data.date instanceof Date ? data.date.getTime() : 0))
        };
      });
      // Sort in memory to avoid index requirements
      docs.sort((a, b) => (b.date || 0) - (a.date || 0));
      setPurchases(docs);
      setPurchasesLoaded(true);
    }, (err) => {
      console.error('Error in purchases subscription:', err);
      setPurchasesLoaded(true);
    });

    return unsubscribe;
  }, [user]);

  // Handle purchases subscription
  useEffect(() => {
    const unsubscribe = subscribePurchases();
    return () => unsubscribe();
  }, [subscribePurchases]);

  // Handle live sync for documents
  useEffect(() => {
    if (!user) {
      setAllDocuments([]);
      setDocumentsLoaded(false);
      setUsers([]);
      setUsersLoaded(false);
      setLogs([]);
      setLogsLoaded(false);
      setSecurityAlerts([]);
      setSecurityAlertsLoaded(false);
      return;
    }

    const qDocs = query(collection(db, "documents"), orderBy("uploadedAt", "desc"));
    const unsubscribeDocs = onSnapshot(
      qDocs,
      (snapshot) => {
        const docs = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
          firestoreId: doc.id,
        }));
        setAllDocuments(docs);
        setDocumentsLoaded(true);
      },
      (error) => {
        console.error("Live sync error (docs) in DataContext:", error);
        setDocumentsLoaded(true);
      }
    );

    return () => {
      unsubscribeDocs();
    };
  }, [user]);

  // Initialize and poll data (60s loop)
  useEffect(() => {
    if (!user) return;

    const loadData = async () => {
      setLoading(true);
      const promises = [
        fetchDashboardStats(),
        fetchSecurityMetrics(),
        fetchUsers(),
        fetchRequests()
      ];
      
      if (user.role === 'system_admin' || user.role === 'sysadmin') {
        promises.push(fetchLogs());
        promises.push(fetchSecurityAlerts());
      }
      
      await Promise.all(promises);
      setLoading(false);
      setNextRefresh(60);
    };

    loadData();

    const timer = setInterval(() => {
      setNextRefresh((prev) => {
        if (prev <= 1) {
          fetchDashboardStats();
          fetchSecurityMetrics();
          fetchUsers();
          fetchRequests();
          if (user.role === 'system_admin') {
            fetchLogs();
            fetchSecurityAlerts();
          }
          setRefreshCycle(c => c + 1);
          return 60;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [user, fetchDashboardStats, fetchSecurityMetrics, fetchUsers, fetchLogs, fetchSecurityAlerts, fetchRequests]);

  // Memoize stable data values
  const dataValue = useMemo(() => ({
    allDocuments,
    documentsLoaded,
    users,
    usersLoaded,
    logs,
    logsLoaded,
    securityAlerts,
    securityAlertsLoaded,
    requests,
    requestsLoaded,
    purchases,
    purchasesLoaded,
    stats,
    recentUsers,
    statsLoaded,
    securityMetricsLoaded,
    loading,
    refreshStats: fetchDashboardStats,
    refreshSecurityMetrics: fetchSecurityMetrics,
    refreshUsers: fetchUsers,
    refreshLogs: fetchLogs,
    refreshSecurityAlerts: fetchSecurityAlerts,
    refreshRequests: fetchRequests,
    updateSecurityAlertStatus,
    deleteSecurityAlert,
    updateRequestStatus,
    deleteRequest
  }), [
    allDocuments, documentsLoaded, users, usersLoaded, logs, logsLoaded, 
    securityAlerts, securityAlertsLoaded, requests, requestsLoaded, purchases, purchasesLoaded, stats, recentUsers, statsLoaded, 
    securityMetricsLoaded, loading, fetchDashboardStats, fetchSecurityMetrics, 
    fetchUsers, fetchLogs, fetchSecurityAlerts, fetchRequests,
    updateSecurityAlertStatus, deleteSecurityAlert, updateRequestStatus, deleteRequest
  ]);

  // Memoize volatile refresh values
  const refreshValue = useMemo(() => ({
    nextRefresh,
    refreshCycle
  }), [nextRefresh, refreshCycle]);

  return (
    <DataContext.Provider value={dataValue}>
      <RefreshContext.Provider value={refreshValue}>
        {children}
      </RefreshContext.Provider>
    </DataContext.Provider>
  );
};
